import { useCallback, useMemo } from "react"

import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Button, Divider, Dropdown, DropdownItem, DropdownMenu, Icon } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"
import { styled } from "styled-components"

import api from "api"
import { UserChooser } from "components/lib/Choosers"
import { AssessorAudienceWarning } from "components/lib/Choosers/UserChooser/Content/ChallengeAudienceWarning"
import { useAppSelector } from "store"
import util from "utils/utils"

import IdeaAssessmentReport from "../../IdeaAssessmentReport"

const SubSegment = styled.div`
  display: flex;
  justify-content: space-between;
  background-color: #f7f7f7;
  border-radius: 5px;
  padding: 5px;
`

const SubSegmentContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 5px;
`

type AssessorManagementProps = {
  idea: OpenAPI.GET<"/ideas/{id}">["response"]
  challenge: OpenAPI.GET<"/challenges/{id}">["response"]
}

export const AssessorManagement = ({ idea, challenge }: AssessorManagementProps) => {
  const { t } = useTranslation()
  const user = useAppSelector((state) => state.user)

  const template = challenge?.ownerIdeaTemplate
  const assessmentRounds = template?.assessments?.rounds || []

  const addAssessors = useCallback(
    (users, assessmentRoundId: string) => {
      const usersToAdd = users.filter((assessor) => !assessor.isEmailInvitee)
      if (usersToAdd.length === 0) {
        return
      }
      api.challenges.assignAssessors(
        challenge._id,
        [idea._id],
        usersToAdd.map((u) => u._id),
        assessmentRoundId,
        ({ ideas }) => {
          const updatedIdea = ideas[0]
          util.onUpdateIdea(idea, {
            assessments: updatedIdea.assessments,
            isAssessor: updatedIdea.isAssessor,
          })
          toast.success("Assessors updated")
        },
        (err) => toast.error(err.message),
      )
    },
    [idea, challenge?._id],
  )

  const canEditAssessments =
    util.canManageChallenge(user, challenge) || util.hasPermission(user, "challenge.editAssessments", idea.challenge)

  return (
    <div>
      {canEditAssessments ? (
        <>
          <SubSegment>
            <SubSegmentContent>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                {assessmentRounds.length > 1 ? (
                  <Dropdown
                    pointing="top right"
                    icon={null}
                    trigger={
                      <Button
                        size="small"
                        content={
                          <p>
                            <Icon name="user plus" /> Assign assessors
                          </p>
                        }
                      />
                    }
                  >
                    <DropdownMenu>
                      {assessmentRounds.map((round) => (
                        <UserChooser
                          key={round.id}
                          trigger={<DropdownItem>{round.name}</DropdownItem>}
                          enabledFeatures={{ search: true, invite: true }}
                          onComplete={(users) => {
                            const emailUsers = users.filter((u) => u.isEmailInvitee)
                            if (emailUsers.length) {
                              api.invitations.createBulk(
                                {
                                  invitees: emailUsers.map((u) => u._id),
                                  invitationType: "email",
                                  forType: "ideaAssessor",
                                  forId: idea.challenge,
                                  forIdeas: [idea._id],
                                  ideaDetails: {
                                    [idea._id]: { assessmentRounds: [round.id] },
                                  },
                                },
                                () => {
                                  toast.success(`Invitation${emailUsers.length > 1 ? "s" : ""} sent`)
                                  api.queryClient.invalidateQueries({
                                    queryKey: ["invitations", "ideaAssessor", idea.challenge],
                                  })
                                },
                                () => {},
                              )
                            }
                            addAssessors(users, round.id)
                          }}
                          forId={challenge?._id}
                          afterContent={() =>
                            !challenge?.visibility?.organisations?.includes(user.organisation) ? (
                              <AssessorAudienceWarning />
                            ) : null
                          }
                          clearOnComplete
                          forType="assessor"
                          subtitle={`Choose users to assign as assessors of the selected ${t("generic.ideas")}.`}
                          confirm="Assign these assessors"
                          searchFunction={(term, cb, fail) =>
                            api.search.challengeAssessors(idea.challenge, term, ({ users }) => cb(users), fail)
                          }
                        />
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                ) : (
                  <UserChooser
                    trigger={
                      <Button
                        size="small"
                        content={
                          <p>
                            <Icon name="user plus" /> Assign assessors
                          </p>
                        }
                      />
                    }
                    enabledFeatures={{ search: true, invite: true }}
                    onComplete={(users) => {
                      const emailUsers = users.filter((u) => u.isEmailInvitee)
                      if (emailUsers.length) {
                        api.invitations.createBulk(
                          {
                            invitees: emailUsers.map((u) => u._id),
                            invitationType: "email",
                            forType: "ideaAssessor",
                            forId: idea.challenge,
                            forIdeas: [idea._id],
                            ideaDetails: {
                              [idea._id]: { assessmentRounds: [assessmentRounds[0].id] },
                            },
                          },
                          () => {
                            toast.success(`Invitation${emailUsers.length > 1 ? "s" : ""} sent`)
                            api.queryClient.invalidateQueries({
                              queryKey: ["invitations", "ideaAssessor", idea.challenge],
                            })
                          },
                          () => {},
                        )
                      }
                      addAssessors(users, assessmentRounds[0].id)
                    }}
                    forId={challenge?._id}
                    afterContent={() =>
                      !challenge?.visibility?.organisations?.includes(user.organisation) ? (
                        <AssessorAudienceWarning />
                      ) : null
                    }
                    clearOnComplete
                    forType="assessor"
                    subtitle={`Choose users to assign as assessors of the selected ${t("generic.ideas")}.`}
                    confirm="Assign these assessors"
                    searchFunction={(term, cb, fail) =>
                      api.search.challengeAssessors(idea.challenge, term, ({ users }) => cb(users), fail)
                    }
                  />
                )}
              </div>
            </SubSegmentContent>
          </SubSegment>
          <Divider hidden />
        </>
      ) : null}
      <IdeaAssessmentReport idea={idea} challenge={challenge} fixedHeight={null} />
    </div>
  )
}
